import React, { useState, useEffect } from 'react';
import {
    Save, Share2, Download, Play, Plus, Sparkles,
    Settings, ArrowLeft, AlignJustify, Table,
    Maximize, Minimize, PanelRightClose, PanelRightOpen, Loader2,
    LayoutTemplate, UploadCloud, MessageSquare, FunctionSquare, History, Tag
} from 'lucide-react';

const HeaderButton = ({ icon: Icon, label, onClick, variant = 'secondary', disabled = false, className = '', title = '' }) => {
    const baseStyle = "flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded transition-all select-none h-8 border-0";
    const variants = {
        primary: "bg-[#118DF0] hover:bg-[#0070c8] text-white",
        secondary: "bg-transparent text-[#252423] dark:text-slate-300 hover:bg-[#e1dfdd] dark:hover:bg-slate-700",
        ai: "bg-transparent text-indigo-600 hover:bg-[#e1dfdd]",
        ghost: "bg-transparent text-[#605e5c] dark:text-slate-400 hover:bg-[#e1dfdd] dark:hover:bg-slate-700"
    };

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`${baseStyle} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            title={title || label}
        >
            {Icon && <Icon className={`w-3.5 h-3.5 ${variant === 'ai' ? 'text-indigo-500' : ''}`} />}
            {label && <span>{label}</span>}
        </button>
    );
};

export const DashboardHeader = ({
    title = "New Dashboard",
    onAction,
    onTitleChange,
    onToggleCompact,
    selectedDataset,
    onPreviewData,
    isFullScreen,
    isSidebarOpen,
    onToggleSidebar,
    onSave,
    onPublish, // New Prop
    onHeaderSettings, // New Prop
    onBack,
    isSaving = false,
    titleStyle = {} as any, // New Prop for Rich Text
    activeVersion = null as string | null, // e.g. 'v2' — the currently live published version
}) => {
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [tempTitle, setTempTitle] = useState(title);
    const [isCompact, setIsCompact] = useState(false);

    useEffect(() => {
        setTempTitle(title);
    }, [title]);

    const handleTitleSubmit = () => {
        setIsEditingTitle(false);
        if (tempTitle.trim()) {
            onTitleChange && onTitleChange(tempTitle);
        } else {
            setTempTitle(title);
        }
    };

    const handleCompactToggle = () => {
        const newState = !isCompact;
        setIsCompact(newState);
        onToggleCompact && onToggleCompact(newState);
    };

    return (
        <div className="h-14 bg-[#f3f3f3] dark:bg-slate-800 border-b border-[#d6d6d6] dark:border-slate-700 flex items-center justify-between px-4 z-10 relative" style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

            {/* Left: Identity — matches Flow Studio layout */}
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center gap-1 px-2 py-1.5 hover:bg-[#e1dfdd] dark:hover:bg-slate-700 rounded text-[#605e5c] dark:text-slate-400 transition-colors text-sm font-medium"
                    title="Back to Dashboard List"
                >
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <h1 className="font-semibold text-[#252423] dark:text-slate-200 mr-1 flex-shrink-0 text-sm">Dashboard:</h1>
                {isEditingTitle ? (
                    <input
                        type="text"
                        className="text-sm font-medium text-[#252423] dark:text-slate-100 border-b border-[#118DF0] focus:outline-none bg-transparent min-w-[160px]"
                        value={tempTitle}
                        onChange={(e) => setTempTitle(e.target.value)}
                        onBlur={handleTitleSubmit}
                        onKeyDown={(e) => e.key === 'Enter' && handleTitleSubmit()}
                        autoFocus
                    />
                ) : (
                    <div className="flex items-center gap-1.5">
                        {/* Click title to rename */}
                        <span
                            className="select-none text-[#252423] dark:text-slate-100 hover:text-[#118DF0] transition-colors text-sm font-medium cursor-pointer"
                            title="Click to rename"
                            onClick={() => setIsEditingTitle(true)}
                            style={{
                                color: titleStyle?.color || undefined,
                                fontWeight: titleStyle?.bold ? 'bold' : 500,
                                fontStyle: titleStyle?.italic ? 'italic' : 'normal',
                                textDecoration: titleStyle?.underline ? 'underline' : 'none',
                            }}
                        >
                            {title}
                        </span>
                        {/* Active version badge */}
                        {activeVersion ? (
                            <span
                                title={`Active published version: ${activeVersion}`}
                                className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-400 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-600 dark:text-emerald-300 leading-none shadow-sm flex-shrink-0 whitespace-nowrap"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 animate-pulse"></span>
                                {activeVersion}
                            </span>
                        ) : (
                            <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-600 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-400 leading-none flex-shrink-0 whitespace-nowrap">
                                Draft
                            </span>
                        )}
                        {/* Settings button */}
                        <button
                            type="button"
                            title="Edit dashboard name & description"
                            onClick={onHeaderSettings}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-[#a19f9d] hover:bg-[#e1dfdd] dark:hover:bg-slate-700 hover:text-[#605e5c] dark:text-slate-500 dark:hover:text-slate-200 transition-colors border border-transparent hover:border-[#edebe9] dark:hover:border-slate-600"
                        >
                            <Settings className="w-3 h-3" />
                            Settings
                        </button>
                    </div>
                )}
            </div>

            {/* Center: Toolbar Actions */}
            <div className="flex items-center gap-0.5">

                {/* Layout Toggle */}
                <HeaderButton
                    icon={isCompact ? AlignJustify : LayoutTemplate}
                    label={isCompact ? "Auto Layout" : "Free Layout"}
                    onClick={handleCompactToggle}
                    title={isCompact ? "Switch to Free Placement" : "Switch to Auto-Stacking"}
                    className={isCompact ? "text-blue-600 bg-blue-50 border-blue-200" : ""}
                />

                {/* Divider */}
                <div className="w-px h-5 bg-[#d6d6d6] dark:bg-slate-600 mx-1" />

                {/* Data Preview */}
                <button
                    onClick={onPreviewData}
                    disabled={!selectedDataset}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-[#252423] dark:text-slate-300 hover:bg-[#e1dfdd] dark:hover:bg-slate-700 rounded transition-all disabled:opacity-50 h-8"
                    title="Preview Data"
                >
                    <Table className="w-3.5 h-3.5" />
                    <span>Data Preview</span>
                </button>

                {/* New Measure */}
                <HeaderButton
                    icon={FunctionSquare}
                    label="New Measure"
                    variant="secondary"
                    onClick={() => onAction && onAction('newMeasure')}
                />

                {/* Divider */}
                <div className="w-px h-5 bg-[#d6d6d6] dark:bg-slate-600 mx-1" />

                {/* Versions — styled outline like Flow Studio */}
                <button
                    onClick={() => onAction && onAction('versionHistory')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-md border border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 dark:bg-teal-900/10 dark:border-teal-800 dark:text-teal-300 dark:hover:bg-teal-900/20 transition-colors h-8"
                    title="View version history and load snapshots"
                >
                    <History className="w-3.5 h-3.5" />
                    Versions
                </button>

                {/* Divider */}
                <div className="w-px h-5 bg-[#d6d6d6] dark:bg-slate-600 mx-1" />

                {/* Save */}
                <HeaderButton
                    icon={isSaving ? Loader2 : Save}
                    label="Save"
                    onClick={onSave}
                    disabled={isSaving}
                    className="min-w-[64px]"
                />

                {/* Export */}
                <HeaderButton
                    icon={Download}
                    label="Export"
                    variant="ghost"
                    onClick={() => onAction && onAction('export')}
                />

                {/* Publish — styled like Flow Studio */}
                <button
                    onClick={onPublish}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-md bg-[#118DF0] hover:bg-[#0070c8] text-white transition-colors h-8 ml-1 shadow-sm"
                    title="Publish dashboard"
                >
                    <UploadCloud className="w-3.5 h-3.5" />
                    Publish
                </button>

                {/* Share */}
                <HeaderButton
                    icon={Share2}
                    label="Share"
                    onClick={() => { }}
                />

                {/* Divider */}
                <div className="flex items-center gap-0.5 ml-1 pl-2 border-l border-[#d6d6d6] dark:border-slate-600">
                    {/* Sidebar Toggle */}
                    <button
                        onClick={() => onToggleSidebar && onToggleSidebar(!isSidebarOpen)}
                        className={`p-1.5 rounded transition-colors ${!isSidebarOpen ? 'bg-[#deecf9] text-[#118DF0]' : 'text-[#605e5c] hover:bg-[#e1dfdd]'}`}
                        title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
                    >
                        {isSidebarOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
                    </button>
                    {/* Full Screen Toggle */}
                    <button
                        onClick={() => onAction && onAction('fullscreen')}
                        className="p-1.5 rounded transition-colors hover:bg-[#e1dfdd] dark:hover:bg-slate-700 text-[#605e5c]"
                        title="Toggle Full Screen"
                    >
                        {isFullScreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                    </button>
                </div>

            </div>
        </div >
    );

};
